<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()
import { useDatePickerLocalization } from '../../composables/useDatePickerLocalization'
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, computed } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import { Grid, type GridSortChangeEvent } from '@progress/kendo-vue-grid';
import { loadMessages, LocalizationProvider } from '@progress/kendo-vue-intl';
import { EventCreate, EventGuestCreate, EventOpenGetList, EventClosedGetList,
          EventTypesGetList, AddressAdd } from '../../services/EventService.ts'
import { AddressBookCreate } from '../../services/AddressBookService.ts'
import type { IEvent, IEventType, IAoeHost } from '../../types/EventTypes'
import { getEventTypeLabelKey } from '../../constants/EventType'
import type { SortDescriptor } from '@progress/kendo-data-query'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { orderBy } from '@progress/kendo-data-query';
import { formatDateAsDDMMYYYY, formatDateTimeForJSONRequest } from '../../api/Common.ts';
import { TextBox as KTextBox, RadioButton, type RadioButtonChangeEvent } from '@progress/kendo-vue-inputs'
import ApplicationField from '../../application/ApplicationField.vue'
import PhoneInputControl from '../PhoneInputControl.vue'
import { createRequiredFieldValidator, createEmailFieldValidator, createNameFieldValidator, createPhoneFieldValidator } from '../../validators/StringValidator.ts'
import {
  Form as kForm,
  FormElement as kformelement,
  type FormSubmitClickEvent
} from '@progress/kendo-vue-form'
import { DatePicker, TimePicker, type DatePickerChangeEvent } from '@progress/kendo-vue-dateinputs';
import AddressControl from './AddressControl.vue'
import { ConsultantGet } from '../../api/Consultant.ts'
import { GetBeeNumberAuthenicatedUser, GetEventManagementUrl } from '../../stores/SettingsStore.ts'
import type { IAddressVerificationListItem, IAddress, ICountry } from '../../types/CountryCultureTypes'
import type { IPhoneInput, IPhoneOutput } from '../../types/CommonTypes'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'

interface IGuestPhoneCountry {
  text: string
  id: string
}
import { Popup } from '@progress/kendo-vue-popup'
import VerifyAddressPopup from '../VerifyAddressPopup.vue'
import FindEventContactsControl from './FindEventContactsControl.vue'
import { handleError } from '../../services/ErrorService.ts'
import { FormatCurrency } from '../../services/currencyFormatter.ts'
import { GetCountriesFromLocalStorageOrApi } from '../../services/CountryCultureService.ts'
import Swal from 'sweetalert2'

const logger = useLogger()

const { dateFormat, formatPlaceholder } = useDatePickerLocalization(locale)

const isSubmittingPart2 = ref(false);
setStoreLogger(logger)
logger.debug('AOEEventList.vue')

// Mobile detection
const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}


const verifyAddress = ref<IAddressVerificationListItem[]>([])
const addressVerificationShow = ref<boolean>(false)
const findHostessError = ref<boolean>(false)
const addressVerifyError = ref<boolean>(false)
const showEventTitleError = ref<boolean>(false)
const showEventAddHostError = ref<boolean>(false)
const showHostInfoError = ref<boolean>(false)
const showHostAddressError = ref<boolean>(false)
const showEventLocationAddressError = ref<boolean>(false)
const showEventDateError = ref<boolean>(false)
const showEventAddressError = ref<boolean>(false)
const showEventConsultantError = ref<boolean>(false)
const showEventTypeError = ref<boolean>(false)
const showEventTimeError = ref<boolean>(false)
const showEventPastDateError = ref<boolean>(false)
const newEventVisible = ref<boolean>(false)
const newEventVisibleWizard1 = ref<boolean>(false)
const newEventVisibleWizard2 = ref<boolean>(false)
const newEventVisibleWizard3 = ref<boolean>(false)
const newEventStep2Visible = ref<boolean>(false)
const newEventStep3Visible = ref<boolean>(false)
const findHostessVisible = ref<boolean>(false)
const addHostessVisible = ref<boolean>(false)
const addressControlType = ref<string>('')
const txtEventTitle = ref<string>('')
const txtEventDate = ref<Date>(new Date())
const txtEventStartTime = ref<Date>(new Date(txtEventDate.value.getFullYear(), txtEventDate.value.getMonth(), txtEventDate.value.getDate(), 19))
const txtEventEndTime = ref<Date>(new Date(txtEventDate.value.getFullYear(), txtEventDate.value.getMonth(), txtEventDate.value.getDate(), 20))
const eventTypeCountries = ref<string[]>([])
const eventTypes = ref<IEventType[]>([])
const selectedEventType = ref<number>(0)
const selectedCountry = ref<string>('')
const findHostessType = ref<string>('')
const findHostessId = ref<number>(0)
const hostSearchText = ref<string>('')
const loader = ref<boolean>(false)
const sort = ref<SortDescriptor[]>([{ field: 'eventStartDate', dir: 'desc' }])
const gridData = ref<IEvent[]>([])
const searchText = ref<string>('')
const currentFilter = ref<string>('open')
const countries = ref<ICountry[]>([])
const gridKey = ref(0)
const filteredGridData = computed<IEvent[]>(() => {
  if (!searchText.value.trim()) {
    return gridData.value
  }

  const searchTerm = searchText.value.toLowerCase().trim()
  return gridData.value.filter(event => {
    // Search by event title
    if (event.title?.toLowerCase().includes(searchTerm)) {
      return true
    }

    // Search by event ID
    if (event.eventId?.toString().includes(searchTerm)) {
      return true
    }

    // Search by hostess name
    if (event.hostName?.toLowerCase().includes(searchTerm)) {
      return true
    }

    return false
  })
})
const sortedGridData = computed<IEvent[]>(() => orderBy(filteredGridData.value, sort.value));

// Mobile pagination
const currentPage = ref(1)
const itemsPerPage = ref(5)
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return sortedGridData.value.slice(start, end)
})
const totalPages = computed(() => Math.ceil(sortedGridData.value.length / itemsPerPage.value))

function goToPage(page: number) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    // Scroll to top of mobile card view
    const mobileView = document.querySelector('.event-cards-container')
    if (mobileView) {
      mobileView.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}

function nextPage() {
  goToPage(currentPage.value + 1)
}

function previousPage() {
  goToPage(currentPage.value - 1)
}

const columns = [
        {
          field: 'title',
          title: t('eventList.EventTitle'),
          width: resolveColumnWidth('200px', t('eventList.EventTitle')),
          cell: 'eventTitleTemplate'
        },
        {
          field: 'eventId',
          title: t('eventList.EventId'),
          width: resolveColumnWidth('100px', t('eventList.EventId'))
        },
        {
          field: 'eventStartDate',
          title: t('eventList.EventDate'),
          width: resolveColumnWidth('150px', t('eventList.EventDate')),
          cell: 'eventDateTemplate'
        },
        {
          field: 'hostName',
          title: t('eventList.EventHostess'),
          width: resolveColumnWidth('150px', t('eventList.EventHostess'))
        },
        {
          field: 'eventLocation',
          title: t('eventList.EventLocation'),
          width: resolveColumnWidth('200px', t('eventList.EventLocation')),
          cell: 'eventLocationTemplate'
        },
        {
          field: 'codeIdEventStatus',
          title: t('eventList.EventStatus'),
          width: resolveColumnWidth('120px', t('eventList.EventStatus')),
          cell: 'eventStatusTemplate'
        },
        {
          field: 'partyTotal',
          title: t('eventList.EventTotal'),
          width: resolveColumnWidth('120px', t('eventList.EventTotal')),
          cell: 'partyTotalTemplate'
        },
        {
          field: 'cvEarnedTotal',
          title: t('eventList.EventCommission'),
          width: resolveColumnWidth('120px', t('eventList.EventCommission')),
          cell: 'cvEarnedTotalTemplate'
        }
      ]

// Static fallback map for known country codes → currency (ISO 4217)
// Used when the async countries list hasn't loaded yet
const staticCountryCurrencyMap: Record<string, string> = {
  'DE': 'EUR', 'AT': 'EUR', 'FR': 'EUR',
  'US': 'USD', 'CA': 'USD'
}

// Helper function to get currency from country code
function getCurrencyFromCountryCode(countryCode: string | undefined): string | null {
  if (!countryCode) return null

  // First try the loaded countries list (authoritative)
  if (countries.value.length > 0) {
    const country = countries.value.find(c => c.countryCode === countryCode)
    if (country?.currencyId) return country.currencyId
  }

  // Synchronous fallback: static map — avoids showing USD while countries list loads
  return staticCountryCurrencyMap[countryCode.toUpperCase()] ?? null
}

// Format currency based on event's country
function formatCurrency(value: number, event: IEvent): string {
  if (value === undefined || value === null) return ''

  // Get currency from event's address country code
  const currencyId = getCurrencyFromCountryCode(event.address?.countryCode)

  // Use FormatCurrency utility which handles culture and currency formatting
  const formatted = FormatCurrency(value, currencyId)
  return formatted || value.toString()
}
const hostFirstName = ref<string>('')
const hostLastName = ref<string>('')
const hostEmailAddress = ref<string>('')
const hostAddressLine1 = ref<string>('')
const hostAddressLine2 = ref<string>('')
const hostHouseNumber = ref<string>('')
const hostCity = ref<string>('')
const hostStateCode = ref<string>('')
const hostPostalCode = ref<string>('')
const hostCountryCode = ref<string>('')

const firstNameValidator = [createRequiredFieldValidator(t, 'firstName'), createNameFieldValidator(t, 'firstName')]
const lastNameValidator = [createRequiredFieldValidator(t, 'lastName'), createNameFieldValidator(t, 'lastName')]
const emailValidator = [createEmailFieldValidator(t, 'emailAddress')] // Email is optional (phone OR email)
const myForm = ref(null)
const hostAddressControl = ref<InstanceType<typeof AddressControl> | null>(null)
const eventLocationAddressControl = ref<InstanceType<typeof AddressControl> | null>(null)
const addressControlKey = ref(0)
const hostPhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)
const hostPhoneModel = ref<IPhoneOutput | null>(null)
const hostPhoneCountries = ref<IGuestPhoneCountry[]>([])

function newHostPhoneModel(): IPhoneOutput {
  return {
    number: '',
    formattedNumber: '',
    countryCode: selectedCountry.value || 'US',
    codeIdPhoneType: 'HOME',
    extension: ''
  }
}

function applyDefaultHostPhoneCountry() {
  const code = selectedCountry.value
  if (!code) return
  // Only seed the default when the user hasn't entered a number yet
  if (!hostPhoneModel.value || !hostPhoneModel.value.number) {
    hostPhoneModel.value = newHostPhoneModel()
  }
}

function loadHostPhoneCountries() {
  GetCountriesFromLocalStorageOrApi().then(data => {
    if (data?.items) {
      const list: IGuestPhoneCountry[] = []
      for (const country of data.items) {
        list.push({ text: country.countryCode, id: country.countryCode })
      }
      list.sort((a, b) => a.text.localeCompare(b.text))
      hostPhoneCountries.value = list
      applyDefaultHostPhoneCountry()
    }
  })
}
const formInitialValues = ref<IAoeHost>({
  beeNumber: 0,
  firstName: '',
  lastName: '',
  emailAddress: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateCode: '',
  postalCode: '',
  countryCode: ''
} as IAoeHost)
//   const formProperties = {
//   mode: 'create',
// } as ApplicationEditFormProperties
const form2InitialValues = ref({ })
const selectedHostType = ref<string>('host')
const eventManagementURL = ref<string>('')
const createdEventIdToken = ref<string>('')

const beeNumberAuth = ref<number | null>(0)
const consultantTimeZone = ref<string>('')
const eventLocation = ref<string>('atHost')
const eventLocationAddressLine1 = ref<string>('')
const eventLocationAddressLine2 = ref<string | null>('')
const eventLocationAddressHouseNumber = ref<string | null>('')
const eventLocationAddressCity = ref<string>('')
const eventLocationAddressStateCode = ref<string | null>('')
const eventLocationAddressPostalCode = ref<string>('')
const eventLocationAddressCountryCode = ref<string>('')
const eventLocationAddressId = ref<number | null>(0)

// Store host address for non-consultant hosts (including addressId)
interface ISavedHostAddress extends IAddress {
  addressId?: number | null
}
const savedHostAddress = ref<ISavedHostAddress | null>(null)

const urlParams = new URLSearchParams(window.location.search)
const isNewEvent = urlParams.get('isNewEvent')

onMounted(async () => {
  logger.debug('onMounted')

  // Load Kendo Grid localized messages
  loadKendoMessages()

  // Initialize mobile detection
  checkMobile()
  window.addEventListener('resize', checkMobile)

  GetEventManagementUrl().then(url =>
  {
    eventManagementURL.value = url + "?event="
  })

  EventTypesGetList().then((eventTypesResp) => {
    if (eventTypesResp)
    {
      for (const eventType of eventTypesResp.items) {
        eventTypes.value.push(eventType)
      }
    }
  })

  // Load countries data for currency formatting
  GetCountriesFromLocalStorageOrApi().then((countriesResp) => {
    if (countriesResp?.items) {
      countries.value = countriesResp.items
      logger.debug('Countries loaded for currency formatting:', countries.value.length)
    }
  })

  // Load phone countries for host phone input
  loadHostPhoneCountries()

  if (isNewEvent === 'true')
  {
    logger.error('isNewEvent is true')
    newEventVisible.value = true;
    newEventVisibleWizard1.value = true
  }

  GetOpenEventList()

  SetEventLocationForConsultantHost()
})

function sortChangeHandler (e: GridSortChangeEvent) {
  loader.value = true;
  if (e.sort[0] !== undefined) {
    sort.value = e.sort;
  }
  else
  {
    logger.debug('sortChangeHandler: e.sort[0] is undefined')
  }
  loader.value = false;
  HideNoRecordsWhileLoading()
}

function ReLoadGrid()
{
  loader.value = true;
  gridData.value = []
  sort.value = [{ field: 'eventStartDate', dir: 'desc' }]
  HideNoRecordsWhileLoading()
}

function HideNoRecordsWhileLoading()
{
  const eventListDiv = document.getElementById('eventListGridRowDiv') as HTMLElement
  if (!eventListDiv) {
    return
  }
  const classAttribute = eventListDiv.attributes.getNamedItem('class');
  if (classAttribute) {
    if (loader.value == false)
    {
      classAttribute.value = 'row mt-3'
    }
    else
    {
      classAttribute.value = 'row mt-3 grid-loading'
    }
  }
}

function GetOpenEventList()
{
  currentFilter.value = 'open'
  ReLoadGrid()
  EventOpenGetList().then((eventResp) => {
    if (eventResp)
    {
      for (const event of eventResp.items) {
          gridData.value.push(event)
      }
    loader.value = false;
    HideNoRecordsWhileLoading()
    }
  }).catch(async error => {
    await handleError(error, t)
  })
}

function GetClosedEventList()
{
  currentFilter.value = 'closed'
  ReLoadGrid()
  EventClosedGetList().then((eventResp) => {
    if (eventResp)
    {
      for (const event of eventResp.items) {
        gridData.value.push(event)
      }
    loader.value = false;
    HideNoRecordsWhileLoading()
    }
  })
}

async function radioBtnChangeEventTypeHandler(e: RadioButtonChangeEvent, eventType: IEventType)
{
  logger.debug('radioBtnChangeEventTypeHandler')
  newEventStep2Visible.value = true
  eventTypeCountries.value = eventType.countries
  selectedEventType.value = eventType.eventTypeId

  // Auto-select consultant's country if available in the event type countries
  await setDefaultCountrySelection()
}

async function setDefaultCountrySelection()
{
  logger.debug('setDefaultCountrySelection')

  // Get consultant's country from their profile
  try {
    beeNumberAuth.value = await GetBeeNumberAuthenicatedUser();
    if (beeNumberAuth.value !== null && beeNumberAuth.value !== 0) {
      const consultantResp = await ConsultantGet(beeNumberAuth.value);
      if (consultantResp?.item?.countryCode) {
        const consultantCountry = consultantResp.item.countryCode;

        // Check if consultant's country is available in the event type countries
        if (eventTypeCountries.value.includes(consultantCountry)) {
          selectedCountry.value = consultantCountry;
          newEventStep3Visible.value = true;
          logger.debug('Auto-selected consultant country:', consultantCountry);
        }
      }
    }
  } catch (error) {
    logger.error('Failed to get consultant country for auto-selection:', error);
  }
}

function radioBtnChangeEventTypeCountryHandler(e: RadioButtonChangeEvent, countryCode: string)
{
  logger.debug('radioBtnChangeEventTypeCountryHandler')
  newEventStep3Visible.value = true
  selectedCountry.value = countryCode
}

function radioBtnChangeEventTypeHostHandler(host: string)
{
  logger.debug('radioBtnChangeEventTypeHostHandler', host)

  selectedHostType.value = host;

  if (host === 'find')
  {
    findHostessVisible.value = true
  }
  else
  {
    findHostessVisible.value = false
    findHostessId.value = 0;
    findHostessType.value = '';
  }

  if (host === 'add')
  {
    addHostessVisible.value = true
    // Initialize host address country code if not set
    if (!hostCountryCode.value) {
      hostCountryCode.value = selectedCountry.value
    }
    // Initialize form values with current reactive variable values
    syncFormInitialValues()
  }
  else
  {
    addHostessVisible.value = false
    // Reset form initial values when leaving "add" mode to clear validation state
    formInitialValues.value = {
      beeNumber: 0,
      firstName: '',
      lastName: '',
      emailAddress: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      stateCode: '',
      postalCode: '',
      countryCode: ''
    }
    // Also clear host-specific error flags
    showHostInfoError.value = false
    showHostAddressError.value = false
  }
}

function dateChangeHandler(event: DatePickerChangeEvent) {
  logger.debug('dateChangeHandler event:', event)
  if (event.value != null)
  {
    txtEventDate.value = event.value
    txtEventStartTime.value = new Date(txtEventDate.value.getFullYear(), txtEventDate.value.getMonth(), txtEventDate.value.getDate(), txtEventStartTime.value.getHours(), txtEventStartTime.value.getMinutes())
    txtEventEndTime.value = new Date(txtEventDate.value.getFullYear(), txtEventDate.value.getMonth(), txtEventDate.value.getDate(), txtEventEndTime.value.getHours(), txtEventEndTime.value.getMinutes())

    // Clear date and time validation errors when date is changed
    showEventDateError.value = false
    showEventTimeError.value = false
    showEventPastDateError.value = false

    // Also reset submission state if it was stuck due to validation errors
    if (isSubmittingPart2.value) {
      isSubmittingPart2.value = false
    }
  }
}

function onSubmitClickHandler(event: FormSubmitClickEvent) {
  // Let Kendo validate first - if it fails, event.preventDefault() is called automatically
  // If Kendo validation passes, then do our custom validation
  if (event.isValid === false) {
    logger.debug('Kendo validation failed')
    return
  }
  performSubmit(event)
}

function onSubmitForm2ClickHandler(event: FormSubmitClickEvent) {
  if (isSubmittingPart2.value) return;
  isSubmittingPart2.value = true;
  logger.debug('onSubmitForm2ClickHandler event:', event)

  if (eventLocation.value !== 'atHost')
  {
    // Validate event location address fields before proceeding
    if (!validateEventLocationAddress()) {
      showEventLocationAddressError.value = true
      isSubmittingPart2.value = false
      return
    }
    showEventLocationAddressError.value = false

    addressControlType.value = 'eventLocation'
    addAddress(false).then(() => {
      if (verifyAddress.value.length > 0 || addressVerifyError.value == true) {
        addressVerificationShow.value = true;
        isSubmittingPart2.value = false;
        // Scroll to top when popup opens
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      else
      {
        SubmitEvent();
        isSubmittingPart2.value = false;
      }
    }).catch(() => {
      isSubmittingPart2.value = false;
    })
  }
  else
  {
    SubmitEvent()
  }
}

async function SubmitEvent()
{
  if (txtEventTitle.value.length == 0)
  {
    showEventTitleError.value = true
    return
  }

  if (txtEventStartTime.value == undefined)
  {
    showEventDateError.value = true
    return
  }

  // Validate that start time is not after end time
  if (txtEventStartTime.value >= txtEventEndTime.value)
  {
    showEventTimeError.value = true
    return
  }
  else
  {
    showEventTimeError.value = false
  }

  // Validate that event is not in the past (date comparison only — time of day is irrelevant)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const startDate = new Date(txtEventStartTime.value)
  startDate.setHours(0, 0, 0, 0)
  if (startDate < today)
  {
    showEventPastDateError.value = true
    return
  }
  else
  {
    showEventPastDateError.value = false
  }

  if (eventLocationAddressId.value == 0 || eventLocationAddressId.value == null)
  {
    showEventAddressError.value = true
    return
  }

  if (beeNumberAuth.value == null || beeNumberAuth.value == 0)
  {
    beeNumberAuth.value = await GetBeeNumberAuthenicatedUser();
    if (beeNumberAuth.value == null || beeNumberAuth.value == 0)
    {
      showEventConsultantError.value = true
      return
    }
  }

  if (selectedEventType.value == 0)
  {
    showEventTypeError.value = true
    return
  }

  if (beeNumberAuth.value != null)
  {
    EventCreate({
        beeNumber: beeNumberAuth.value,
        codeIdEventStatus: "O",
        eventStartDate: formatDateTimeForJSONRequest(txtEventStartTime.value),
        eventEndDate: formatDateTimeForJSONRequest(txtEventEndTime.value),
        addressId: eventLocationAddressId.value == 0 ? null : eventLocationAddressId.value,
        eventTimeZone: consultantTimeZone.value,
        eventTypeId: selectedEventType.value,
        title: txtEventTitle.value,
      }).then((event) => {
        logger.error('event:', event, 'selectedHostType:', selectedHostType.value)
        if (event.eventIdToken != '' && event.eventIdToken != null)
        {
          //add the host
          if (selectedHostType.value === 'host' && beeNumberAuth.value != null)
          {
            EventAddGuest(event.eventIdToken, 'H', beeNumberAuth.value, null)
          }
          else if (selectedHostType.value === 'find' && findHostessId.value > 0 && findHostessType.value == 'bee' && beeNumberAuth.value != null)
          {
            EventAddGuest(event.eventIdToken, 'H', findHostessId.value, null)
          }
          else if (selectedHostType.value === 'find' && findHostessId.value > 0 && findHostessType.value == 'addressBook' && beeNumberAuth.value != null)
          {
            EventAddGuest(event.eventIdToken, 'H', null, findHostessId.value)
          }
          else if (selectedHostType.value === 'add' && findHostessId.value > 0 && beeNumberAuth.value != null)
          {
            EventAddGuest(event.eventIdToken, 'H', null, findHostessId.value)
          }
        }
      })
  }
}

async function EventAddGuest(eventIdToken: string, role: string, guestBeeNumber: number | null, addressBookId: number | null)
{
  EventGuestCreate(
  {
    codeIdEventRole: role,
    eventIdToken: eventIdToken,
    guestBeeNumber: guestBeeNumber,
    addressBookId: addressBookId
  }).then((eventGuest) => {
    if (eventGuest)
    {
      // Store the event ID token and show the final wizard step
      createdEventIdToken.value = eventIdToken;
      newEventVisibleWizard2.value = false;
      newEventVisibleWizard3.value = true;
    }
  })
}

async function performSubmit(event: FormSubmitClickEvent) {
  logger.debug('performSubmit event:', event)

  if (selectedHostType.value === 'host')
  {
    newEventVisibleWizard1.value = false
    newEventVisibleWizard2.value = true

    SetEventLocationForConsultantHost()

    return
  }
  else if (selectedHostType.value === 'find' && findHostessId.value == 0)
  {
    findHostessError.value = true
    return
  }
  else if (selectedHostType.value === 'find' && findHostessId.value > 0)
  {
    newEventVisibleWizard1.value = false
    newEventVisibleWizard2.value = true
    return
  }
  else if (selectedHostType.value === 'add')
  {
    // Validate phone field (contact info already validated by Kendo for firstName, lastName, email)
    const phoneValue = hostPhoneModel.value?.number?.trim() || ''
    const emailValue = hostEmailAddress.value?.trim() || ''
    
    if (!phoneValue && !emailValue) {
      showHostInfoError.value = true
      logger.debug('Host info validation failed - need phone or email')
      return
    }
    
    // If phone is entered, validate it using PhoneInputControl's validation
    if (phoneValue && hostPhoneControl.value) {
      if (!hostPhoneControl.value.validate()) {
        showHostInfoError.value = true
        logger.debug('Host phone validation failed')
        return
      }
    }
    
    showHostInfoError.value = false
    
    // Validate address fields
    if (!validateHostAddress()) {
      logger.debug('Host address validation failed')
      return
    }

    // Sync form values before proceeding
    syncFormInitialValues()
    AddHostToAddressBook()
    return
  }


  // if (event.isModified == false && formProperties.mode === 'create') {
  //   logger.debug('Form is not modified')
  //   return
  // } else if (event.isValid === false) {
  //   logger.debug('Form is not valid')
  //   return
  // } else {
  //   alert('can be submitted')
  // }
}

async function AddHostToAddressBook()
{
  // Validate host address fields are populated
  if (hostAddressLine1.value.length == 0 || hostCity.value.length == 0 || hostPostalCode.value.length == 0 || hostCountryCode.value.length == 0)
  {
    logger.error('Host address fields are empty:', {
      addressLine1: hostAddressLine1.value,
      city: hostCity.value,
      postalCode: hostPostalCode.value,
      countryCode: hostCountryCode.value
    })
    showHostAddressError.value = true
    return
  }

  // Check if we already have a host address book entry (prevents duplicate creation on back/forward navigation)
  if (findHostessId.value > 0) {
    logger.debug('Host address book entry already exists, proceeding to next step')
    newEventVisibleWizard1.value = false
    newEventVisibleWizard2.value = true
    return
  }

  logger.debug('Host address fields before copying to eventLocation:', {
    addressLine1: hostAddressLine1.value,
    addressLine2: hostAddressLine2.value,
    houseNumber: hostHouseNumber.value,
    city: hostCity.value,
    stateCode: hostStateCode.value,
    postalCode: hostPostalCode.value,
    countryCode: hostCountryCode.value
  })

  // Copy host address fields to event location address fields for processing
  eventLocationAddressLine1.value = hostAddressLine1.value
  eventLocationAddressLine2.value = hostAddressLine2.value
  eventLocationAddressHouseNumber.value = hostHouseNumber.value
  eventLocationAddressCity.value = hostCity.value
  eventLocationAddressStateCode.value = hostStateCode.value
  eventLocationAddressPostalCode.value = hostPostalCode.value
  eventLocationAddressCountryCode.value = hostCountryCode.value

  addressControlType.value = 'hostAdd'
  addAddress(false).then(() => {
      if (verifyAddress.value.length > 0 || addressVerifyError.value == true) {
        addressVerificationShow.value = true;
        // Scroll to top when popup opens
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      else
      {
        AddressBookAdd()
      }
    })
}

async function AddressBookAdd()
{
  const phoneValue = hostPhoneModel.value?.number?.trim() || ''
  const payload: any = {
    firstName: hostFirstName.value,
    lastName: hostLastName.value,
    emailAddress: hostEmailAddress.value,
    addressIdShipping: eventLocationAddressId.value
  }
  
  // Only include phone if a number was entered
  if (phoneValue) {
    payload.primaryPhone = {
      number: phoneValue,
      countryCode: hostPhoneModel.value?.countryCode || eventLocationAddressCountryCode.value,
      codeIdPhoneType: 'HOME'
    }
  }
  
  AddressBookCreate(payload).then((addressBookResp) => {
            if (addressBookResp)
            {
              findHostessId.value = addressBookResp.addressBookId

              // Save the host address for later restoration (including addressId)
              savedHostAddress.value = {
                firstName: null,
                lastName: null,
                addressLine1: eventLocationAddressLine1.value,
                addressLine2: eventLocationAddressLine2.value,
                houseNumber: eventLocationAddressHouseNumber.value,
                city: eventLocationAddressCity.value,
                stateCode: eventLocationAddressStateCode.value,
                postalCode: eventLocationAddressPostalCode.value,
                countryCode: eventLocationAddressCountryCode.value,
                addressId: eventLocationAddressId.value
              }

              newEventVisibleWizard1.value = false
              newEventVisibleWizard2.value = true
            }
        }).catch(async error => {
          logger.debug('AddressBookCreate failed:', error)
          await handleError(error, t)
        })
}

async function SetEventLocationForConsultantHost()
{
  beeNumberAuth.value = await GetBeeNumberAuthenicatedUser();
    if (beeNumberAuth.value !== null && beeNumberAuth.value !== 0)
    {
      ConsultantGet(beeNumberAuth.value).then((consultantResp) => {
        if (consultantResp)
        {
          if (selectedHostType.value === 'host')
          {
            eventLocationAddressLine1.value = consultantResp.item.shippingAddress.addressLine1
            eventLocationAddressLine2.value = consultantResp.item.shippingAddress.addressLine2
            eventLocationAddressHouseNumber.value = consultantResp.item.shippingAddress.houseNumber
            eventLocationAddressCity.value = consultantResp.item.shippingAddress.city
            eventLocationAddressStateCode.value = consultantResp.item.shippingAddress.stateCode
            eventLocationAddressPostalCode.value = consultantResp.item.shippingAddress.postalCode
            eventLocationAddressCountryCode.value = consultantResp.item.shippingAddress.countryCode
            eventLocationAddressId.value = consultantResp.item.shippingAddress.addressId
          }
          consultantTimeZone.value = consultantResp.item.timeZone
        }
      })
    }
}

function radioBtnChangeEventLocationHandler(e: RadioButtonChangeEvent, location: string)
{
  logger.debug('radioBtnChangeEventLocationHandler', location)
  eventLocation.value = location

  // Force AddressControl re-render to clear internal validation state
  addressControlKey.value++

  if (location !== 'atHost')
  {
    eventLocationAddressLine1.value = ''
    eventLocationAddressLine2.value = ''
    eventLocationAddressHouseNumber.value = ''
    eventLocationAddressCity.value = ''
    eventLocationAddressStateCode.value = ''
    eventLocationAddressPostalCode.value = ''
    eventLocationAddressCountryCode.value = selectedCountry.value
    eventLocationAddressId.value = 0
    // Clear address error when switching away from host address
    showEventAddressError.value = false
    showEventLocationAddressError.value = false
  }
  else if (selectedHostType.value === 'host')
  {
    // For consultant hosts, restore their address immediately
    SetEventLocationForConsultantHost()
    // Clear the error immediately since consultant should have valid address
    showEventAddressError.value = false
    showEventLocationAddressError.value = false
  }
  else if (selectedHostType.value === 'find' && findHostessId.value > 0)
  {
    // For selected hosts from FindEventContactsControl, restore from savedHostAddress
    if (savedHostAddress.value !== null) {
      eventLocationAddressLine1.value = savedHostAddress.value.addressLine1
      eventLocationAddressLine2.value = savedHostAddress.value.addressLine2
      eventLocationAddressHouseNumber.value = savedHostAddress.value.houseNumber
      eventLocationAddressCity.value = savedHostAddress.value.city
      eventLocationAddressStateCode.value = savedHostAddress.value.stateCode
      eventLocationAddressPostalCode.value = savedHostAddress.value.postalCode
      eventLocationAddressCountryCode.value = savedHostAddress.value.countryCode

      // Restore the saved address ID
      if (savedHostAddress.value.addressId && savedHostAddress.value.addressId > 0) {
        eventLocationAddressId.value = savedHostAddress.value.addressId
        showEventAddressError.value = false
        logger.debug('Restored find host address with saved ID:', savedHostAddress.value.addressId)
      } else {
        eventLocationAddressId.value = 0
        logger.debug('No saved address ID found for find host')
      }
    } else {
      logger.debug('No saved address available for find host')
      eventLocationAddressId.value = 0
    }
  }
  else if (savedHostAddress.value !== null)
  {
    // Restore saved host address for non-consultant hosts (including addressId)
    eventLocationAddressLine1.value = savedHostAddress.value.addressLine1
    eventLocationAddressLine2.value = savedHostAddress.value.addressLine2
    eventLocationAddressHouseNumber.value = savedHostAddress.value.houseNumber
    eventLocationAddressCity.value = savedHostAddress.value.city
    eventLocationAddressStateCode.value = savedHostAddress.value.stateCode
    eventLocationAddressPostalCode.value = savedHostAddress.value.postalCode
    eventLocationAddressCountryCode.value = savedHostAddress.value.countryCode

    // Restore the saved address ID directly
    if (savedHostAddress.value.addressId && savedHostAddress.value.addressId > 0) {
      eventLocationAddressId.value = savedHostAddress.value.addressId
      showEventAddressError.value = false
      logger.debug('Restored host address with saved ID:', savedHostAddress.value.addressId)
    } else {
      // If no saved address ID, keep it as 0 and let validation handle it
      eventLocationAddressId.value = 0
      logger.debug('No saved address ID found, validation will be required')
    }
  }
  else if (location === 'atHost')
  {
    // If switching to atHost but no saved address and not consultant host, this is an error state
    eventLocationAddressId.value = 0
    logger.debug('No saved address available for host location')
    // Clear error flags when switching to atHost
    showEventAddressError.value = false
    showEventLocationAddressError.value = false
  }
}

async function addAddress(skipAddressValidation: boolean)
{
  verifyAddress.value = []
  const address: IAddress = {
        firstName: null,
        lastName: null,
        addressLine1: eventLocationAddressLine1.value,
        addressLine2: eventLocationAddressLine2.value,
        houseNumber: eventLocationAddressHouseNumber.value,
        city: eventLocationAddressCity.value,
        stateCode: eventLocationAddressStateCode.value,
        postalCode: eventLocationAddressPostalCode.value,
        countryCode: eventLocationAddressCountryCode.value,
      }

      logger.debug('AddressAdd - addressControlType:', addressControlType.value)
      logger.debug('AddressAdd - address object:', address)
      await AddressAdd({
        address: address,
        skipAddressValidation: skipAddressValidation,
      }).then(response => {
        logger.debug('AddressAdd response:', response)
        for (const item of response.items) {
          verifyAddress.value.push(item);
        }

        if (response.addressId == null)
        {
          addressVerifyError.value = true
        }
        else
        {
          addressVerifyError.value = false
        }

        // Set address ID if we have a valid response, especially when skipping validation
        if (response.addressId && (skipAddressValidation || (verifyAddress.value.length == 0 && addressVerifyError.value == false))) {
          eventLocationAddressId.value = response.addressId
        }
      })

      if (verifyAddress.value.length > 0 || addressVerifyError.value == true) {
        addressVerificationShow.value = true;
      }
      else
      {
        addressVerificationShow.value = false;
      }
}

function useEnteredAddressClick() {
  logger.debug('useEnteredAddressClick')

  addAddress(true).then(() => {
    addressVerificationShow.value = false;
    showEventAddressError.value = false;
    logger.debug('eventLocationAddressId', eventLocationAddressId.value)
    if (addressControlType.value != 'hostAdd')
    {
      SubmitEvent();
      isSubmittingPart2.value = false;
    }
    else
    {
      AddressBookAdd()
    }
  })
}

function useSelectedAddressClick(selectedAddress: IAddress) {
  logger.debug('useSelectedAddressClick', selectedAddress)

  eventLocationAddressLine1.value = selectedAddress.addressLine1
  eventLocationAddressLine2.value = selectedAddress.addressLine2
  eventLocationAddressHouseNumber.value = selectedAddress.houseNumber
  eventLocationAddressCity.value = selectedAddress.city
  eventLocationAddressStateCode.value = selectedAddress.stateCode
  eventLocationAddressPostalCode.value = selectedAddress.postalCode
  eventLocationAddressCountryCode.value = selectedAddress.countryCode

  addAddress(false).then(() => {
    if (verifyAddress.value.length > 0  || addressVerifyError.value == true) {
      addressVerificationShow.value = true;
      // Scroll to top when popup opens
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    else
    {
      if (addressControlType.value != 'hostAdd')
      {
        SubmitEvent()
      }
      else
      {
        AddressBookAdd()
      }
    }
  })
}

function addressControlUpdateHandler(data: IAddress)
{
  logger.debug('addressControlUpdateHandler', data)
  eventLocationAddressLine1.value = data.addressLine1
  eventLocationAddressLine2.value = data.addressLine2
  eventLocationAddressHouseNumber.value = data.houseNumber
  eventLocationAddressCity.value = data.city
  eventLocationAddressStateCode.value = data.stateCode
  eventLocationAddressPostalCode.value = data.postalCode
  eventLocationAddressCountryCode.value = data.countryCode

  if (eventLocation.value === 'atNew') {
    showEventAddressError.value = false
    showEventLocationAddressError.value = false
    if (isSubmittingPart2.value) {
      isSubmittingPart2.value = false
    }
  }
}

function FindEventContactsControlAddressChangeHandler(data: IAddress)
{
  logger.debug('FindEventContactsControlAddressChangeHandler', data)

  if (data.addressLine1.length == 0)
  {
    eventLocation.value = 'atNew'
    eventLocationAddressCountryCode.value = selectedCountry.value
    eventLocationAddressId.value = 0
  }
  else
  {
    // Set the address ID from the selected contact
    const addressId = 'addressId' in data ? (data as ISavedHostAddress).addressId : null
    if (addressId && addressId > 0) {
      eventLocationAddressId.value = addressId
      logger.debug('Set eventLocationAddressId from selected contact:', addressId)
    } else {
      eventLocationAddressId.value = 0
      logger.debug('No valid addressId found in contact data')
    }
  }

  // Store the host address for later restoration
  if (selectedHostType.value !== 'host' && data.addressLine1.length > 0) {
    savedHostAddress.value = {
      ...data,
      addressId: 'addressId' in data ? (data as ISavedHostAddress).addressId : null
    }
  }

  eventLocationAddressLine1.value = data.addressLine1
  eventLocationAddressLine2.value = data.addressLine2
  eventLocationAddressHouseNumber.value = data.houseNumber
  eventLocationAddressCity.value = data.city
  eventLocationAddressStateCode.value = data.stateCode
  eventLocationAddressPostalCode.value = data.postalCode
  eventLocationAddressCountryCode.value = data.countryCode
}

function FindEventContactsControlHostessChangeHandler(data: { findHostessType: string, findHostessId: number })
{
  logger.debug('FindEventContactsControlHostessChangeHandler', data)

  findHostessError.value = false
  findHostessType.value = data.findHostessType
  findHostessId.value = data.findHostessId
}

function FindEventContactsControlSearchTextChangeHandler(searchText: string)
{
  logger.debug('FindEventContactsControlSearchTextChangeHandler', searchText)
  hostSearchText.value = searchText
}

function handleSaveAndFinish()
{
  logger.debug('handleSaveAndFinish')
  // Remove any query string from the URL before redirecting to account page
  const baseUrl = window.location.href.split('?')[0];
  window.location.href = baseUrl;
}

function handleHostPortal()
{
  logger.debug('handleHostPortal')
  // Redirect to event management URL with the created event ID token
  if (createdEventIdToken.value && eventManagementURL.value) {
    window.location.href = eventManagementURL.value + createdEventIdToken.value;
  }
}

function handleInviteGuests()
{
  logger.debug('handleInviteGuests')
  // Redirect to event management URL with the created event ID token
  if (createdEventIdToken.value && eventManagementURL.value) {
    window.location.href = eventManagementURL.value + createdEventIdToken.value;
  }
}

function clearTimeValidationErrors()
{
  logger.debug('clearTimeValidationErrors')
  showEventTimeError.value = false
  showEventPastDateError.value = false

  // Also reset submission state if it was stuck due to validation errors
  if (isSubmittingPart2.value) {
    isSubmittingPart2.value = false
  }
}

function clearEventTitleError()
{
  logger.debug('clearEventTitleError')
  showEventTitleError.value = false

  // Also reset submission state if it was stuck due to validation errors
  if (isSubmittingPart2.value) {
    isSubmittingPart2.value = false
  }
}

function clearSearch()
{
  logger.debug('clearSearch - clearing search text')
  searchText.value = ''
  logger.debug('clearSearch - search text after clear:', searchText.value)
}

function loadKendoMessages() {
  const currentLocale = locale.value || 'en'

  const gridMessages: Record<string, { noRecords: string }> = {
    'en': {
      noRecords: 'No events found'
    },
    'es': {
      noRecords: 'No se encontraron eventos'
    },
    'de': {
      noRecords: 'Keine Events gefunden'
    },
    'fr': {
      noRecords: 'Aucun événement trouvé'
    }
  }

  const currentMessages = gridMessages[currentLocale] || gridMessages['en']

  const messages = {
    'grid.noRecords': currentMessages.noRecords
  }

  loadMessages(messages, currentLocale)

  gridKey.value++
}

function updateHostFirstName(value: string)
{
  hostFirstName.value = value
  // Clear error when user starts typing
  if (showHostInfoError.value) {
    validateHostInfo()
  }
}

function updateHostLastName(value: string)
{
  hostLastName.value = value
  // Clear error when user starts typing
  if (showHostInfoError.value) {
    validateHostInfo()
  }
}

function updateHostEmailAddress(value: string)
{
  hostEmailAddress.value = value
  // Clear error when user starts typing
  if (showHostInfoError.value) {
    validateHostInfo()
  }
}

function handlePhoneChange() {
  // Clear error when user changes phone
  if (showHostInfoError.value) {
    validateHostInfo()
  }
}

const validateHostInfo = (): boolean => {
  if (!hostFirstName.value?.trim()) {
    showHostInfoError.value = true
    return false
  }
  if (!hostLastName.value?.trim()) {
    showHostInfoError.value = true
    return false
  }
  // Require at least one of phone or email
  const phoneValue = hostPhoneModel.value?.number?.trim() || ''
  const emailValue = hostEmailAddress.value?.trim() || ''
  
  if (!phoneValue && !emailValue) {
    showHostInfoError.value = true
    return false
  }
  
  // If phone is entered, validate it using PhoneInputControl's validation
  if (phoneValue && hostPhoneControl.value) {
    if (!hostPhoneControl.value.validate()) {
      showHostInfoError.value = true
      return false
    }
  }
  
  // If email is entered, validate it
  if (emailValue) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue)) {
      showHostInfoError.value = true
      return false
    }
  }
  
  showHostInfoError.value = false
  return true
}

const validateHostAddress = (): boolean => {
  // Use AddressControl's built-in validate() method
  return hostAddressControl.value?.validate() ?? true
}

const validateEventLocationAddress = (): boolean => {
  // Use AddressControl's built-in validate() method
  return eventLocationAddressControl.value?.validate() ?? true
}

function hostAddressControlUpdateHandler(data: IAddress)
{
  logger.debug('hostAddressControlUpdateHandler', data)
  hostAddressLine1.value = data.addressLine1
  hostAddressLine2.value = data.addressLine2 || ''
  hostHouseNumber.value = data.houseNumber || ''
  hostCity.value = data.city
  hostStateCode.value = data.stateCode || ''
  hostPostalCode.value = data.postalCode
  hostCountryCode.value = data.countryCode
  // Clear address error when user changes address fields
  if (showHostAddressError.value) {
    validateHostAddress()
  }
}

function syncFormInitialValues()
{
  // Sync reactive variables with form initial values
  formInitialValues.value.firstName = hostFirstName.value
  formInitialValues.value.lastName = hostLastName.value
  formInitialValues.value.emailAddress = hostEmailAddress.value
  formInitialValues.value.phone = hostPhoneModel.value?.number || ''
  formInitialValues.value.addressLine1 = hostAddressLine1.value
  formInitialValues.value.addressLine2 = hostAddressLine2.value
  formInitialValues.value.city = hostCity.value
  formInitialValues.value.stateCode = hostStateCode.value
  formInitialValues.value.postalCode = hostPostalCode.value
  formInitialValues.value.countryCode = hostCountryCode.value
}

function handlePreviousClick()
{
  logger.debug('handlePreviousClick')

  // Navigate back to wizard step 1
  newEventVisible.value = true
  newEventVisibleWizard1.value = true
  newEventVisibleWizard2.value = false

  // Scroll to top of the page
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function resetNewEventForm()
{
  logger.debug('resetNewEventForm')

  // Reset error states
  showEventTitleError.value = false
  showEventAddHostError.value = false
  showEventDateError.value = false
  showEventAddressError.value = false
  showEventConsultantError.value = false
  showEventTypeError.value = false
  showEventTimeError.value = false
  showEventPastDateError.value = false
  findHostessError.value = false
  addressVerifyError.value = false

  // Reset submission state
  isSubmittingPart2.value = false

  // Reset wizard visibility states
  newEventStep2Visible.value = false
  newEventStep3Visible.value = false
  findHostessVisible.value = false
  addHostessVisible.value = false

  // Reset form data
  txtEventTitle.value = ''
  txtEventDate.value = new Date()
  txtEventStartTime.value = new Date(txtEventDate.value.getFullYear(), txtEventDate.value.getMonth(), txtEventDate.value.getDate(), 19)
  txtEventEndTime.value = new Date(txtEventDate.value.getFullYear(), txtEventDate.value.getMonth(), txtEventDate.value.getDate(), 20)

  // Reset event type and country selections
  selectedEventType.value = 0
  selectedCountry.value = ''
  eventTypeCountries.value = []

  // Reset host information
  selectedHostType.value = 'host'
  findHostessType.value = ''
  findHostessId.value = 0
  hostSearchText.value = ''
  hostFirstName.value = ''
  hostLastName.value = ''
  hostEmailAddress.value = ''
  hostPhoneModel.value = newHostPhoneModel()
  hostAddressLine1.value = ''
  hostAddressLine2.value = ''
  hostHouseNumber.value = ''
  hostCity.value = ''
  hostStateCode.value = ''
  hostPostalCode.value = ''
  hostCountryCode.value = ''

  // Reset form initial values
  formInitialValues.value = {
    beeNumber: 0,
    firstName: '',
    lastName: '',
    emailAddress: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateCode: '',
    postalCode: '',
    countryCode: ''
  }

  // Reset event location
  eventLocation.value = 'atHost'
  eventLocationAddressLine1.value = ''
  eventLocationAddressLine2.value = ''
  eventLocationAddressHouseNumber.value = ''
  eventLocationAddressCity.value = ''
  eventLocationAddressStateCode.value = ''
  eventLocationAddressPostalCode.value = ''
  eventLocationAddressCountryCode.value = ''
  eventLocationAddressId.value = 0

  // Reset other states
  addressControlType.value = ''
  verifyAddress.value = []
  addressVerificationShow.value = false
  createdEventIdToken.value = ''
  savedHostAddress.value = null
}

</script>

<template>
 <div>
    <div v-if="!newEventVisible">
      <div class="row">
        <div class="col-6">
          <p class="pageHeader">
            {{ t('eventList.MyEvents') }}
          </p>
        </div>
        <div class="col-6 newEventButtonDiv">
          <button class="btn primaryBtn" @click="newEventVisible = true; newEventVisibleWizard1 = true">
            {{ t('eventList.NewEventButton') }}
          </button>
        </div>
      </div>
      <div class="row mb-4">
        <div class="col-12">
          <div class="event-controls-container">
            <!-- Filter Buttons -->
            <div class="filter-buttons-group">
              <button
                class="filter-btn filter-btn-open"
                :class="{ 'active': currentFilter === 'open' }"
                @click="GetOpenEventList"
              >
                {{ t('eventList.OpenEventButton') }}
              </button>
              <button
                class="filter-btn filter-btn-closed"
                :class="{ 'active': currentFilter === 'closed' }"
                @click="GetClosedEventList"
              >
                {{ t('eventList.ClosedEventButton') }}
              </button>
            </div>

            <!-- Search Bar -->
            <div class="search-container">
              <div class="search-input-wrapper">
                <KTextBox
                  v-model="searchText"
                  :placeholder="t('eventList.SearchEvents')"
                  class="search-input-field"
                />
                <button
                  v-if="searchText.trim()"
                  class="clear-search-btn"
                  @click.stop="clearSearch"
                  :title="t('eventList.ClearSearch')"
                  type="button"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="eventListGridRowDiv" class="row mt-3">
        <div class="col-12">
          <!-- Desktop Grid View -->
          <LocalizationProvider :language="locale">
            <Grid
              v-if="!isMobile"
              ref="grid"
              :key="gridKey"
              :data-items="sortedGridData"
              :resizable="true"
              :sortable="true"
              :sort="sort"
              :pageable="false"
              :columns="columns"
              @sortchange="sortChangeHandler"
              :loader="loader"
            >
          <template v-slot:eventTitleTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="eventTitleDiv">
                <a class="event-title-link base-hyper-link" :href="eventManagementURL + props.dataItem.eventIdToken">{{ props.dataItem.title }}</a>
              </td>
            </template>
            <template v-slot:eventDateTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="text-center">
                {{ formatDateAsDDMMYYYY(props.dataItem.eventStartDate) }}
              </td>
            </template>
            <template v-slot:eventLocationTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'">
                {{ props.dataItem.address != undefined ? props.dataItem.address.city + ',' : '' }} {{ props.dataItem.address != undefined ? props.dataItem.address.stateCode : '' }}
              </td>
            </template>
            <template v-slot:eventStatusTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'">
                {{ props.dataItem.codeIdEventStatus?.localizedValue || '' }}
              </td>
            </template>
            <template v-slot:partyTotalTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="text-end">
                {{ formatCurrency(props.dataItem.partyTotal, props.dataItem) }}
              </td>
            </template>
            <template v-slot:cvEarnedTotalTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="text-end">
                {{ formatCurrency(props.dataItem.cvEarnedTotal, props.dataItem) }}
              </td>
            </template>
          </Grid>

          <!-- Mobile Card View -->
          <div v-else class="event-cards-container">
            <div v-if="loader" class="text-center p-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">{{$t('labels.Loading')}}</span>
              </div>
            </div>
            <div v-else-if="sortedGridData.length === 0" class="text-center p-4 text-muted">
              {{ t('eventList.NoEventsFound') }}
            </div>
            <div v-else class="event-cards">
              <div
                v-for="event in paginatedData"
                :key="event.eventId"
                class="event-card"
              >
                <div class="event-card-header">
                  <a class="event-card-title" :href="eventManagementURL + event.eventIdToken">
                    {{ event.title }}
                  </a>
                  <span class="event-card-status">
                    {{ event.codeIdEventStatus?.localizedValue || '' }}
                  </span>
                </div>

                <div class="event-card-body">
                  <div class="event-card-info">
                    <div class="event-card-info-item">
                      <span class="event-card-label">{{ t('eventList.EventId') }}:</span>
                      <span class="event-card-value">{{ event.eventId }}</span>
                    </div>
                    <div class="event-card-info-item">
                      <span class="event-card-label">{{ t('eventList.EventDate') }}:</span>
                      <span class="event-card-value">{{ formatDateAsDDMMYYYY(event.eventStartDate) }}</span>
                    </div>
                    <div class="event-card-info-item">
                      <span class="event-card-label">{{ t('eventList.EventHostess') }}:</span>
                      <span class="event-card-value">{{ event.hostName }}</span>
                    </div>
                    <div v-if="event.address" class="event-card-info-item">
                      <span class="event-card-label">{{ t('eventList.EventLocation') }}:</span>
                      <span class="event-card-value">
                        {{ event.address.city }}{{ event.address.stateCode ? ', ' + event.address.stateCode : '' }}
                      </span>
                    </div>
                    <div class="event-card-info-item">
                      <span class="event-card-label">{{ t('eventList.EventTotal') }}:</span>
                      <span class="event-card-value event-card-amount">{{ formatCurrency(event.partyTotal, event) }}</span>
                    </div>
                    <div class="event-card-info-item">
                      <span class="event-card-label">{{ t('eventList.EventCommission') }}:</span>
                      <span class="event-card-value event-card-amount">{{ formatCurrency(event.cvEarnedTotal, event) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Mobile Pagination Controls -->
              <div v-if="totalPages > 1" class="mobile-pagination">
                <div class="pagination-info">
                  {{ t('rma.Showing') }} {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage, sortedGridData.length) }} {{ t('rma.Of') }} {{ sortedGridData.length }}
                </div>
                <div class="pagination-controls">
                  <button
                    :disabled="currentPage === 1"
                    @click="goToPage(1)"
                    class="pagination-page-btn pagination-nav-btn"
                    :title="t('rma.FirstPage')"
                  >
                    &laquo;
                  </button>
                  <button
                    :disabled="currentPage === 1"
                    @click="previousPage"
                    class="pagination-page-btn pagination-nav-btn"
                    :title="t('rma.PreviousPage')"
                  >
                    &lt;
                  </button>
                  <div class="pagination-current">
                    {{ currentPage }} {{ t('rma.Of') }} {{ totalPages }}
                  </div>
                  <button
                    :disabled="currentPage === totalPages"
                    @click="nextPage"
                    class="pagination-page-btn pagination-nav-btn"
                    :title="t('rma.NextPage')"
                  >
                    &gt;
                  </button>
                  <button
                    :disabled="currentPage === totalPages"
                    @click="goToPage(totalPages)"
                    class="pagination-page-btn pagination-nav-btn"
                    :title="t('rma.LastPage')"
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            </div>
          </div>
          </LocalizationProvider>
        </div>
      </div>
    </div>
    <div v-if="newEventVisibleWizard1">
      <kForm
            :initialValues="formInitialValues"
            :key="JSON.stringify(formInitialValues)"
            @submitclick="onSubmitClickHandler($event)"
            id="myForm"
            ref="myForm"
          >
            <kformelement>
          <div class="row">
            <div class="col-12">
              <p class="pageHeader">
                {{ t('eventList.CreateNewEvents') }}
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <p class="pageSubHeader">
                {{ t('eventList.NewEventStep1') }}
              </p>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 option-radio-row">
              <div
                class="EventTypeInformation"
                v-for="eventType in eventTypes"
                :key="eventType.eventTypeId">
                <RadioButton
                    :name="'group1'"
                    size="large"
                    :id="'btnEventType_' + eventType.eventTypeId"
                    class="radioBtnEventType"
                    :checked="selectedEventType === eventType.eventTypeId"
                    @change="radioBtnChangeEventTypeHandler($event, eventType)"
                    ><label :for="'btnEventType_' + eventType.eventTypeId" class="custom-label">{{t(getEventTypeLabelKey(eventType.eventType))}}</label></RadioButton>
              </div>
            </div>
          </div>
          <hr>
          <div v-if="newEventStep2Visible">
            <div class="row mt-3">
            <div class="col-12">
              <p class="pageSubHeader">
                {{ t('eventList.NewEventStep2') }}
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-12 option-radio-row">
              <div
                class="EventTypeInformation"
                v-for="country in eventTypeCountries"
                :key="country">
                <RadioButton
                    :name="'group2'"
                    size="large"
                    :id="'btnEventTypeCountry_' + country"
                    class="radioBtnEventType"
                    :checked="selectedCountry === country"
                    @change="radioBtnChangeEventTypeCountryHandler($event, country)"
                    ><label :for="'btnEventTypeCountry_' + country" class="custom-label">{{country}}</label></RadioButton>
              </div>
            </div>
          </div>
          </div>
          <hr v-if="newEventStep2Visible">
          <div v-if="newEventStep3Visible">
            <div class="row mt-3">
              <div class="col-12">
                <p class="pageSubHeader">
                  {{ t('eventList.NewEventStep3') }}
                </p>
              </div>
            </div>
            <div class="row">
              <div class="col-12 option-radio-row">
                <div class="EventTypeInformation">
                  <RadioButton
                      :name="'group3'"
                      size="large"
                      id="btnFindHostess"
                      class="radioBtnEventType"
                      :checked="selectedHostType === 'find'"
                      @change="radioBtnChangeEventTypeHostHandler('find')"
                      ><label for="btnFindHostess" class="custom-label">{{t('eventList.FindHost')}}</label></RadioButton>
                </div>
                <div class="EventTypeInformation">
                  <RadioButton
                      :name="'group3'"
                      size="large"
                      id="btnAddHostess"
                      class="radioBtnEventType"
                      :checked="selectedHostType === 'add'"
                      @change="radioBtnChangeEventTypeHostHandler('add')"
                      ><label for="btnAddHostess" class="custom-label">{{t('eventList.AddHost')}}</label></RadioButton>
                </div>
                <div class="EventTypeInformation">
                  <RadioButton
                      :name="'group3'"
                      size="large"
                      id="btnHostess"
                      :checked="selectedHostType === 'host'"
                      class="radioBtnEventType"
                      @change="radioBtnChangeEventTypeHostHandler('host')"
                      ><label for="btnHostess" class="custom-label">{{t('eventList.IAmHost')}}</label></RadioButton>
                </div>
              </div>
            </div>
            <div v-if="findHostessVisible">
              <FindEventContactsControl
                :showAddGuest="false"
                :eventidtoken="null"
                :beeNumberAuth="null"
                :allowMultipleSelection="false"
                :initialSearchText="hostSearchText"
                :initialSelectedHostId="findHostessId"
                :initialSelectedHostType="findHostessType"
                @address-change="FindEventContactsControlAddressChangeHandler"
                @find-hostess="FindEventContactsControlHostessChangeHandler"
                @search-text-change="FindEventContactsControlSearchTextChangeHandler"
              ></FindEventContactsControl>
              <div v-if="findHostessError" class="row mt-3">
                <div class="col-12 errorFindHost">
                  {{ t('eventList.FindHostError') }}
                </div>
              </div>
            </div>
            <div v-if="addHostessVisible" class="addHostSection">
              <fieldset class="k-form-fieldset">
                <div class="row mt-3">
                  <div class="col-12 eventHostLabel">
                    {{ t ('eventList.HostInformation') }}:
                  </div>
                </div>
                <div v-if="showHostInfoError" class="row mt-3">
                  <div class="col-12 errorEventHostAdd">
                    {{ t('eventList.HostInfoError') }}
                  </div>
                </div>
                <div v-if="showHostAddressError" class="row mt-3">
                  <div class="col-12 errorEventHostAdd">
                    {{ t('eventList.HostAddressError') }}
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6">
                    <ApplicationField
                      columnName="firstName"
                      :validator="firstNameValidator"
                      :addAsterisk="true"
                      :keyField="false"
                      @change="updateHostFirstName($event.value)"
                    ></ApplicationField>
                  </div>
                  <div class="col-12 col-md-6">
                      <ApplicationField
                        columnName="lastName"
                        :validator="lastNameValidator"
                        :addAsterisk="true"
                        :keyField="false"
                        @change="updateHostLastName($event.value)"
                      ></ApplicationField>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6">
                    <ApplicationField
                      columnName="emailAddress"
                      :validator="emailValidator"
                      :addAsterisk="false"
                      :keyField="false"
                      @change="updateHostEmailAddress($event.value)"
                    ></ApplicationField>
                  </div>
                  <div class="col-12 col-md-6">
                      <PhoneInputControl
                        ref="hostPhoneControl"
                        class="hostPhoneField"
                        v-model="hostPhoneModel"
                        :required="false"
                        :showExtension="false"
                        :showPhoneType="false"
                        :countryList="hostPhoneCountries"
                        @change="handlePhoneChange"
                      />
                  </div>
                </div>
                <div class="row mt-3">
                  <div class="col-12">
                    <hr/>
                  </div>
                </div>
                <div class="row mt-3">
                  <div class="col-12 eventHostLabel">
                    {{ t ('eventList.HostAddress') }}:
                  </div>
                </div>
                <AddressControl
                  ref="hostAddressControl"
                  :countryCode="hostCountryCode || selectedCountry"
                  :addressLine1="hostAddressLine1"
                  :addressLine2="hostAddressLine2"
                  :houseNumber="hostHouseNumber"
                  :city="hostCity"
                  :stateCode="hostStateCode"
                  :postalCode="hostPostalCode"
                  :isdisabled="false"
                  :columnClass="'col-12 col-md-6'"
                  :showfullnamefield="false"
                  :name="null"
                  @address-change="hostAddressControlUpdateHandler">
                </AddressControl>
              </fieldset>
            </div>
          </div>
          <div class="d-flex justify-content-end mt-3">
                <div class="p-1 cancelButtonDiv">
                  <button class="btnCancel" @click="resetNewEventForm(); newEventVisible = false; newEventVisibleWizard1 = false; newEventVisibleWizard2 = false">
                    {{ t('eventList.CancelButton') }}
                  </button>
                </div>
                <div class="submitButtonDiv p-1">
                  <KButton
                    ref="btnSubmitPart1"
                    id="btnSubmitPart1"
                    type="submit"
                    class="btnSubmitPart1"
                    :rounded="'large'"
                    :theme-color="'checkoutContinue'"
                    :disabled="!newEventStep3Visible"
                    >{{t('eventList.NextButton')}}</KButton
                  >
                </div>
              </div>
        </kformelement>
      </kForm>
    </div>
    <div v-if="newEventVisibleWizard2">
      <kForm
            :initialValues="form2InitialValues"
            :key="JSON.stringify(form2InitialValues)"
            @submitclick="onSubmitForm2ClickHandler($event)"
            id="myForm2"
            ref="myForm2"
          >
        <kformelement>
          <div class="row">
            <div class="col-12">
              <p class="pageHeader">
                {{ t('eventList.CreateNewEvents') }}
              </p>
            </div>
          </div>
          <div v-if="showEventConsultantError" class="row mt-3">
            <div class="col-12 errorEventConsultant">
              {{ t('eventList.EventConsultantError') }}
            </div>
          </div>
          <div v-if="showEventTypeError" class="row mt-3">
            <div class="col-12 errorEventType">
              {{ t('eventList.EventTypeError') }}
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <p class="pageSubHeader">
                {{ t('eventList.NewEventStep4') }}
              </p>
            </div>
          </div>
          <div v-if="showEventTitleError" class="row mt-3">
            <div class="col-12 errorEventTitle">
              {{ t('eventList.EventTitleError') }}
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 col-md-3">
              <label>{{ t('eventList.EventTitle') }}:</label>
            </div>
            <div class="col-12 col-md-9">
              <KTextBox
                v-model="txtEventTitle"
                :rounded="'large'"
                :theme-color="'primary'"
                @input="clearEventTitleError"
              ></KTextBox>
            </div>
          </div>
          <div v-if="showEventDateError" class="row mt-3">
            <div class="col-12 errorEventDate">
              {{ t('eventList.EventDateError') }}
            </div>
          </div>
          <div v-if="showEventTimeError" class="row mt-3">
            <div class="col-12 errorEventTime">
              {{ t('eventList.EventTimeError') }}
            </div>
          </div>
          <div v-if="showEventPastDateError" class="row mt-3">
            <div class="col-12 errorEventPastDate">
              {{ t('eventList.EventPastDateError') }}
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 col-md-2">
              <label>{{ t('eventList.EventDate') }}:</label>
            </div>
            <div class="col-12 col-md-10 mb-3 mb-md-0">
              <LocalizationProvider :language="locale">
                <DatePicker
                  :style="{ width: '100%', maxWidth: '230px' }"
                  :default-value="new Date()"
                  @change="dateChangeHandler($event)"
                  :format="dateFormat"
                  :formatPlaceholder="formatPlaceholder">
                </DatePicker>
              </LocalizationProvider>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 col-md-2">
              <label>{{ t('eventList.EventStartTime') }}:</label>
            </div>
            <div class="col-12 col-md-4 mb-3 mb-md-0">
              <TimePicker :style="{ width: '100%', maxWidth: '230px' }" v-model="txtEventStartTime" @change="clearTimeValidationErrors" />
            </div>
            <div class="col-12 col-md-2">
              <label>{{ t('eventList.EventEndTime') }}:</label>
            </div>
            <div class="col-12 col-md-4">
              <TimePicker :style="{ width: '100%', maxWidth: '230px' }" v-model="txtEventEndTime" @change="clearTimeValidationErrors" />
            </div>
          </div>
          <hr>
          <div>
            <div class="row mt-3">
              <div class="col-12">
                <p class="pageSubHeader">
                  {{ t('eventList.NewEventStep5') }}
                </p>
              </div>
            </div>
            <div v-if="showEventAddressError" class="row mt-3">
              <div class="col-12 errorEventAddress">
                {{ t('eventList.EventAddressError') }}
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12 col-md-6 mb-2 mb-md-0">
                <div class="EventTypeInformation">
                  <RadioButton
                      :name="'group3'"
                      size="large"
                      id="btnAtHostAddress"
                      class="radioBtnEventType"
                      :checked="true"
                      @change="radioBtnChangeEventLocationHandler($event, 'atHost')"
                      ><label for="btnAtHostAddress" class="custom-label">{{t('eventList.AtHostAddress')}}</label></RadioButton>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="EventTypeInformation">
                  <RadioButton
                      :name="'group3'"
                      size="large"
                      id="btnAtNewAddress"
                      class="radioBtnEventType"
                      :checked="eventLocation == 'atNew'"
                      @change="radioBtnChangeEventLocationHandler($event, 'atNew')"
                      ><label for="btnAtNewAddress" class="custom-label">{{t('eventList.AtNewAddress')}}</label></RadioButton>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12">
              <label>{{ t('eventList.EventLocation') }}:</label>
            </div>
          </div>
          <AddressControl
            :key="addressControlKey"
            ref="eventLocationAddressControl"
            :countryCode="eventLocationAddressCountryCode"
            :addressLine1="eventLocationAddressLine1"
            :addressLine2="eventLocationAddressLine2"
            :houseNumber="eventLocationAddressHouseNumber"
            :city="eventLocationAddressCity"
            :stateCode="eventLocationAddressStateCode"
            :postalCode="eventLocationAddressPostalCode"
            :isdisabled="eventLocation === 'atHost'"
            :columnClass="'col-12 col-md-6'"
            :showfullnamefield="false"
            :name="null"
            @address-change="addressControlUpdateHandler">
          </AddressControl>
          <div v-if="showEventLocationAddressError" class="row mt-3">
            <div class="col-12 errorEventHostAdd">
              {{ t('eventList.HostAddressError') }}
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 d-flex flex-column flex-md-row justify-content-end gap-2">
              <button class="btnPrevious" @click="handlePreviousClick">
                {{ t('eventList.PreviousButton') }}
              </button>
              <button class="btnCancel" @click="resetNewEventForm(); newEventVisible = false; newEventVisibleWizard1 = false; newEventVisibleWizard2 = false">
                {{ t('eventList.CancelButton') }}
              </button>
              <KButton
                ref="btnSubmitPart2"
                id="btnSubmitPart2"
                type="submit"
                class="btnSubmitPart2"
                :rounded="'large'"
                :theme-color="'checkoutContinue'"
                :disabled="isSubmittingPart2"
                >{{t('eventList.CreateEventButton')}}</KButton
              >
            </div>
          </div>
        </kformelement>
      </kForm>
    </div>
    <div v-if="newEventVisibleWizard3">
      <div class="row">
        <div class="col-12">
          <p class="pageHeader">
            {{ t('eventList.CreateNewEvents') }}
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <p class="pageSubHeader">
            {{ t('eventList.NewEventStep6') }}
          </p>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-12 col-md-4 mb-3 mb-md-0">
          <div class="option-card" @click="handleSaveAndFinish">
            <div class="option-card-content">
              <h4>{{ t('eventList.SaveAndFinish') }}</h4>
              <p>{{ t('eventList.SaveAndFinishDescription') }}</p>
              <button class="btn btn-outline-primary mt-3">
                {{ t('eventList.SaveAndFinish') }}
              </button>
            </div>
          </div>
        </div>
        <div class="col-12 col-md-4 mb-3 mb-md-0">
          <div class="option-card" @click="handleHostPortal">
            <div class="option-card-content">
              <h4>{{ t('eventList.HostPortal') }}</h4>
              <p>{{ t('eventList.HostPortalDescription') }}</p>
              <button class="btn btn-outline-primary mt-3">
                {{ t('eventList.HostPortal') }}
              </button>
            </div>
          </div>
        </div>
        <div class="col-12 col-md-4">
          <div class="option-card" @click="handleInviteGuests">
            <div class="option-card-content">
              <h4>{{ t('eventList.InviteGuests') }}</h4>
              <p>{{ t('eventList.InviteGuestsDescription') }}</p>
              <button class="btn btn-outline-primary mt-3">
                {{ t('eventList.InviteGuests') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Backdrop overlay -->
    <div v-if="addressVerificationShow" class="address-popup-backdrop"></div>

    <Popup
      :show="addressVerificationShow"
      :popup-class="'address-val-popup-content'"
    >
    <VerifyAddressPopup
      :enteredAddress="{
        addressLine1: eventLocationAddressLine1,
        addressLine2: eventLocationAddressLine2,
        houseNumber: eventLocationAddressHouseNumber,
        city: eventLocationAddressCity,
        stateCode: eventLocationAddressStateCode,
        postalCode: eventLocationAddressPostalCode,
        countryCode: eventLocationAddressCountryCode,
        firstName: null,
        lastName: null,
      }"
      :verifyAddress="verifyAddress"
      :verifyAddressError="addressVerifyError"
      @use-entered-address="useEnteredAddressClick"
      @use-selected-address="useSelectedAddressClick"
      @re-enter-address="addressVerificationShow = false">
    </VerifyAddressPopup>
  </Popup>

  </div>
</template>

<style lang="scss">

.cancelButtonDiv
{
  text-align: right;
}

.btnCancel, .btnSubmitPart1, .btnSearch, .btnSubmitPart2, .btnPrevious
{
  background-color: white;
  border-radius: $button-radius;
  padding: $spacing-2 $spacing-8;
  @include typography-body;
  font-style: normal;
  text-align: center;
}

.btnSubmitPart1, .btnSearch, .btnSubmitPart2
{
  background-color: var(--vt-c-teal-dark);
  color: white;
  border-color: var(--vt-c-teal-dark);
}

.addressBookGrid .k-column-title .k-checkbox-wrap
{
  display: none !important;
}

.addressBookGrid .k-checkbox {
  border: solid 1px black !important;
}

.newEventButtonDiv
{
  text-align: right;
}

/* Event Controls Container */
.event-controls-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $spacing-5;
  padding: $card-padding;
  background: var(--vt-c-white-soft);
  border-radius: $radius-lg;
  border: 1px solid #e9ecef;
}

/* Filter Buttons Group */
.filter-buttons-group {
  display: flex;
  background: white;
  border-radius: $radius-md;
  padding: $spacing-1;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.filter-btn {
  padding: $spacing-2-5 $spacing-5;
  border: none;
  background: transparent;
  color: #6c757d;
  @include typography-small;
  @include font-weight-medium;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.filter-btn:hover {
  background: var(--vt-c-white-soft);
  color: #495057;
}

.filter-btn.active {
  background: var(--vt-c-teal-dark);
  color: white;
  box-shadow: 0 2px 4px rgba(var(--vt-c-teal-dark), 0.2);
}

/* Search Container */
.search-container {
  flex: 1;
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-field {
  width: 100%;
}

.search-input-field .k-textbox {
  width: 100%;
  padding: $spacing-3 $spacing-4;
  padding-right: 40px;
  border: 1px solid #dee2e6;
  @include typography-small;
  border-radius: $radius-md;
  background: white;
  transition: all 0.2s ease;
}

.search-input-field .k-textbox:focus {
  border-color: var(--vt-c-teal-dark);
  box-shadow: 0 0 0 3px rgba(var(--vt-c-teal-dark), 0.1);
  outline: none;
}

.search-input-field .k-textbox::placeholder {
  color: #adb5bd;
}

.clear-search-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  background: #6c757d;
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  @include typography-small;
  @include font-weight-bold;
  justify-content: center;
  line-height: 1;
  font-family: Arial, sans-serif;
  transition: all 0.2s ease;
  pointer-events: auto;
  padding: 0;
  margin: 0;
}

.clear-search-btn:hover {
  background: #495057;
}

/* Responsive Design */
@media (max-width: 768px) {
  .event-controls-container {
    flex-direction: column;
    gap: $gap-md;
  }

  .search-container {
    max-width: none;
    width: 100%;
  }

  .filter-buttons-group {
    width: 100%;
    justify-content: center;
  }

  /* Stack buttons vertically on mobile */
  .btnPrevious,
  .btnCancel,
  .btnSubmitPart2 {
    width: 100%;
  }

  /* Make labels full width on mobile */
  .eventHostLabel {
    margin-bottom: 0.5rem;
  }

  /* Adjust date/time pickers for mobile */
  .k-datepicker,
  .k-timepicker {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* Adjust option cards on mobile */
  .option-card {
    margin: 0;
    margin-bottom: 1rem;
  }
}

.aoeEvents {
  .option-radio-row {
    display: flex;
    gap: $spacing-2;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      justify-content: center;
    }

    .EventTypeInformation {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}


.EventTypeInformation {
    width: 140px;
    min-height: 100px;
    height: auto;
    border: 1px solid rgba(black, 0.2);
    margin: 0;
    padding: $spacing-2-5;
}

.EventTypeInformation label {
    padding-top: $spacing-2;
    margin: 0;
    width: 100%;
}

.EventTypeInformation .k-radio {
  align-items: center; /* Center the elements horizontally */
}

.radioBtnEventType
{
  text-align: center;
}

.custom-label {
  display: flex !important;
  flex-direction: column-reverse; /* Reverse the order of label and radio button */
  align-items: center; /* Center the elements horizontally */
  @include typography-body;
}

.eventHostLabel
{
  @include typography-body;
}

.address-val-popup-content {
    position: fixed;
    top: 50%;
    left: 50%;
    padding: $card-padding;
    transform: translate(-50%, -50%);
    z-index: $z-critical; /* Above backdrop */
    max-height: 90vh;
}

.address-popup-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(black, 0.5);
    z-index: $z-critical-backdrop; /* Below popup */
}

.errorFindHost, .errorEventDate, .errorEventTitle, .errorEventAddress, .errorEventConsultant, .errorEventHostAdd, .errorEventType, .errorEventTime, .errorEventPastDate
{
  color: red;
}

.grid-loading .k-grid-norecords .k-table-td
{
  content-visibility: hidden;
  height: 50px;
}

.option-card {
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  padding: $card-padding;
  margin: $spacing-2-5;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: var(--vt-c-white-soft);
  min-height: 200px;
  display: flex;
  align-items: center;
}

.option-card:hover {
  border-color: var(--vt-c-teal-dark);
  box-shadow: 0 2px 8px rgba(var(--vt-c-teal-dark), 0.1);
  transform: translateY(-2px);
}

.option-card-content {
  text-align: center;
  width: 100%;
}

.option-card h4 {
  @include font-weight-semibold;
  color: var(--vt-c-teal-dark);
  margin-bottom: 15px;
}

.option-card p {
  color: #666;
  margin-bottom: 15px;
  line-height: 1.4;
}

.option-card .btn {
  background-color: var(--vt-c-teal-dark);
  border-color: var(--vt-c-teal-dark);
  color: white;
  border-radius: $button-radius;
}

.option-card .btn:hover {
  background-color: #145049;
  border-color: #145049;
  color: white;
}

/* Mobile Event Card Styles */
.event-cards-container {
  width: 100%;
}

.event-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.event-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: $radius-md;
  padding: 1rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(black, 0.1);
}

.event-card:hover {
  box-shadow: 0 4px 8px rgba(black, 0.2);
  transform: translateY(-2px);
}

.event-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  gap: 0.5rem;
}

.event-card-title {
  @include typography-body-large;
  @include font-weight-semibold;
  color: var(--vt-c-teal-dark);
  text-decoration: none;
  flex: 1;
  word-break: break-word;
}

.event-card-title:hover {
  color: #0d3942;
  text-decoration: underline;
}

.event-card-status {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  @include typography-small;
  @include font-weight-medium;
  border-radius: $radius-lg;
  background-color: #e9ecef;
  color: #495057;
  white-space: nowrap;
  flex-shrink: 0;
}

.event-card-body {
  display: flex;
  flex-direction: column;
}

.event-card-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.event-card-info-item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.25rem 0;
  gap: 0.5rem;
}

.event-card-label {
  @include typography-small;
  @include font-weight-semibold;
  color: #6c757d;
  flex-shrink: 0;
}

.event-card-value {
  @include typography-small;
  color: #212529;
  text-align: right;
  word-break: break-word;
}

.event-card-amount {
  @include font-weight-semibold;
  color: var(--vt-c-teal-dark);
}

// Match the PhoneInputControl boxes to the sibling Kendo ApplicationField inputs
// (Email / First Name / Last Name). The `.addHostSection` ancestor raises
// specificity above PhoneInputControl's scoped styles.

// Phone field wrapper - add margin-bottom to match Kendo FieldWrapper's default spacing
.addHostSection .hostPhoneField {
  // Align with sibling Kendo floating-label fields which have
  // padding-top: calc(line-height * font-size) ≈ 20px + FieldWrapper internal margins
  padding-top: $spacing-8;
  margin-bottom: 1rem;
}

// Wrapper - ensure full width
.addHostSection .hostPhoneField .phone-input-group {
  width: 100%;
  height: 32px;
}

// Country select - match Kendo input styling
.addHostSection .hostPhoneField .country-select {
  @include typography-caption;
  height: 32px !important;
  padding: $spacing-2 $spacing-6 $spacing-2 $spacing-2 !important;
  border: 1px solid var(--color-border) !important;
  border-radius: $input-radius 0 0 $input-radius !important;
  border-right: none !important;
  background-color: var(--color-background) !important;
}

// Phone input - match Kendo input styling
.addHostSection .hostPhoneField .phone-input {
  @include typography-small;
  height: 32px !important;
  padding: $spacing-2 $spacing-3 !important;
  border: 1px solid var(--color-border) !important;
  /* !important required: PhoneInputControl's scoped .phone-input declares border-radius with !important, so only !important can override it regardless of selector specificity */
  border-radius: 0 $input-radius $input-radius 0 !important;
  border-left: none !important;
  background-color: var(--color-background) !important;
}

// Separator - blend with border
.addHostSection .hostPhoneField .phone-separator {
  width: 1px !important;
  background-color: var(--color-border) !important;
}

// Focus states - match Kendo focus color
.addHostSection .hostPhoneField .country-select:focus {
  outline: none !important;
  border-color: #0d6efd !important;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.2) !important;
}

.addHostSection .hostPhoneField .phone-input:focus {
  outline: none !important;
  border-color: #0d6efd !important;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.2) !important;
}

// Error states - match Kendo error styling
.addHostSection .hostPhoneField .country-select.error {
  border-color: #dc3545 !important;
}

.addHostSection .hostPhoneField .country-select.error:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25) !important;
}

.addHostSection .hostPhoneField .phone-input.error {
  border-color: #dc3545 !important;
}

.addHostSection .hostPhoneField .phone-input.error:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25) !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .event-cards {
    gap: 0.75rem;
  }

  .event-card {
    padding: 0.875rem;
  }

  .event-card-title {
    @include typography-body;
  }
}

/* Mobile Pagination Styles */
.mobile-pagination {
  margin-top: $spacing-6;
  padding-top: $spacing-4;
  border-top: 1px solid #e0e0e0;
}

.pagination-info {
  @include typography-small;
  color: #666;
  text-align: center;
  margin-bottom: $spacing-4;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-2;
  flex-wrap: wrap;
}

.pagination-current {
  @include typography-small;
  @include font-weight-medium;
  padding: $spacing-2 $spacing-3;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
}

.pagination-page-btn {
  @include typography-small;
  @include font-weight-medium;
  padding: $spacing-2 $spacing-3;
  min-width: 36px;
  background: var(--vt-c-white);
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-page-btn:hover {
  background-color: var(--vt-c-white-soft);
  border-color: var(--vt-c-teal-dark);
}

.pagination-page-btn.active {
  background-color: var(--vt-c-teal-dark);
  color: var(--vt-c-white);
  border-color: var(--vt-c-teal-dark);
}

.pagination-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}


</style>
